import React, { useState, useEffect, useCallback } from 'react';
import { collection, updateDoc, addDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import './AdminParentsView.css';
import './AdminDetailPages.css';
import { adminSchoolIds } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';

// Helper: load parents like iOS — query users for role=Parent by schoolId, then load each parent profile from parents collection by ID
async function loadParentsLikeIOS(schoolId, scoped, ck) {
  const parentUsers = (await dataCacheService.getDocs(ck('users'), collection(db, 'users')))
    .filter(u => u.role === 'Parent')
    .filter(u => !scoped || (schoolId && (u.schoolId || '').toLowerCase() === schoolId.toLowerCase()));

  const parents = [];
  await Promise.all(parentUsers.map(async (parentUser) => {
    try {
      const parentDoc = await dataCacheService.getDoc(ck(`parents/${parentUser.id}`), doc(db, 'parents', parentUser.id));
      if (parentDoc) {
        parents.push({ ...parentDoc, username: parentUser.username, createdAt: parentUser.createdAt });
      } else {
        // Fallback: use user data as parent profile if no parents doc exists
        parents.push({
          id: parentUser.id,
          firstName: parentUser.firstName || '',
          lastName: parentUser.lastName || '',
          fullName: `${parentUser.firstName || ''} ${parentUser.lastName || ''}`.trim(),
          username: parentUser.username || '',
          email: parentUser.email || '',
          phoneNumber: parentUser.phoneNumber || '',
          linkedChildrenIds: parentUser.linkedChildrenIds || [],
          schoolId: parentUser.schoolId || null,
          isActive: parentUser.isActive !== false,
          createdAt: parentUser.createdAt || null,
        });
      }
    } catch (e) {
      console.error('🔥 AdminParents: Error loading parent profile for', parentUser.id, e);
    }
  }));
  return parents;
}

function AdminParentsView({ adminData }) {
  const [parents, setParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editParent, setEditParent] = useState(null);
  const [detailParent, setDetailParent] = useState(null);
  const [loading, setLoading] = useState(true);

  const [newParent, setNewParent] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    linkedChildrenIds: []
  });

  // School-scoped cache key builder — shared with AdminAnalyticsView so tabs
  // reuse each other's fetched collections.
  const ck = (p) => `${adminSchoolIds(adminData).join('|') || 'all'}:${p}`;

  const loadParents = useCallback(async () => {
    console.log('🔥 AdminParents: Loading parents (iOS-style), schoolId:', adminData?.schoolId);
    try {
      const parentsData = await loadParentsLikeIOS(adminData?.schoolId, !!adminData, ck);
      console.log('🔥 AdminParents: Parents loaded', parentsData.length, parentsData);
      setParents(parentsData);
    } catch (error) {
      console.error('🔥 AdminParents: Error loading parents:', error);
    } finally {
      setLoading(false);
    }
  }, [adminData?.schoolId]);

  const loadStudents = useCallback(async () => {
    console.log('🔥 AdminParents: Loading students, schoolId:', adminData?.schoolId);
    try {
      let classesData = await dataCacheService.getDocs(ck('classes'), collection(db, 'classes'));
      if (adminData) {
        const sid = (adminData.schoolId || '').toLowerCase();
        classesData = sid ? classesData.filter(c => (c.schoolId || '').toLowerCase() === sid) : [];
      }
      const perClass = await Promise.all(classesData.map(cls =>
        dataCacheService.getDocs(ck(`classes/${cls.id}/students`), collection(db, 'classes', cls.id, 'students'))
      ));
      const studentsData = perClass.flat().filter(s => s.isActive !== false);
      console.log('🔥 AdminParents: Students loaded', studentsData.length, studentsData);
      setStudents(studentsData);
    } catch (error) {
      console.error('🔥 AdminParents: Error loading students:', error);
    }
  }, [adminData]);

  useEffect(() => {
    loadParents();
    loadStudents();
  }, [loadParents, loadStudents]);

  const getFilteredParents = () => {
    if (!searchText) {
      return parents;
    }
    return parents.filter(parent =>
      parent.fullName?.toLowerCase().includes(searchText.toLowerCase()) ||
      parent.email?.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  const getLinkedChildren = (parent) => {
    if (!parent.linkedChildrenIds || parent.linkedChildrenIds.length === 0) {
      return [];
    }
    const ids = parent.linkedChildrenIds.map(id => String(id).toLowerCase());
    return students.filter(student =>
      ids.includes(String(student.studentId || '').toLowerCase()) ||
      ids.includes(String(student.id || '').toLowerCase()) ||
      ids.includes(String(student.uuid || '').toLowerCase())
    );
  };

  const handleAddParent = async (e) => {
    e.preventDefault();
    try {
      const parentData = {
        ...newParent,
        fullName: `${newParent.firstName} ${newParent.lastName}`,
        isActive: true,
        createdAt: new Date().toISOString(),
        schoolId: adminData?.schoolId || null
      };
      await addDoc(collection(db, 'parents'), parentData);
      dataCacheService.invalidatePath('parents');
      setShowAddForm(false);
      setNewParent({ firstName: '', lastName: '', email: '', phoneNumber: '', linkedChildrenIds: [] });
      loadParents();
    } catch (error) {
      console.error('Error adding parent:', error);
      alert('Failed to add parent');
    }
  };

  const handleEditClick = (parent) => {
    setEditParent({
      ...parent,
      linkedChildrenIds: parent.linkedChildrenIds || []
    });
    setIsEditing(true);
  };

  const handleCloseModal = () => {
    setIsEditing(false);
    setEditParent(null);
  };

  const handleUpdateParent = async (e) => {
    e.preventDefault();
    if (!editParent) return;
    try {
      // Only name/email/phone are editable — linked children are read-only (parents link children at signup)
      await updateDoc(doc(db, 'parents', editParent.id), {
        fullName: `${editParent.firstName || ''} ${editParent.lastName || ''}`.trim(),
        firstName: editParent.firstName,
        lastName: editParent.lastName,
        email: editParent.email,
        phoneNumber: editParent.phoneNumber,
        updatedAt: new Date().toISOString()
      });
      dataCacheService.invalidatePath('parents');
      handleCloseModal();
      loadParents();
    } catch (error) {
      console.error('Error updating parent:', error);
      alert('Failed to update parent');
    }
  };

  const handleDeleteParent = async (parentId) => {
    if (!confirm('Are you sure you want to delete this parent?')) return false;
    try {
      await updateDoc(doc(db, 'parents', parentId), { isActive: false });
      dataCacheService.invalidatePath('parents');
      loadParents();
      return true;
    } catch (error) {
      console.error('Error deleting parent:', error);
      alert('Failed to delete parent');
      return false;
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const filteredParents = getFilteredParents();

  // Edit modal (name/email/phone only — linked children are read-only) — shared by list and detail pages
  const parentEditModal = isEditing && editParent && (
    <div className="modal-overlay" onClick={handleCloseModal}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Edit Parent</h3>
        <form onSubmit={handleUpdateParent}>
          <div className="form-row">
            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                value={editParent.firstName || ''}
                onChange={(e) => setEditParent({ ...editParent, firstName: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                value={editParent.lastName || ''}
                onChange={(e) => setEditParent({ ...editParent, lastName: e.target.value })}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={editParent.email || ''}
                onChange={(e) => setEditParent({ ...editParent, email: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                value={editParent.phoneNumber || ''}
                onChange={(e) => setEditParent({ ...editParent, phoneNumber: e.target.value })}
              />
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={handleCloseModal}>
              Cancel
            </button>
            <button type="submit" className="save-button">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Full-screen parent detail page — replaces the list
  if (detailParent) {
    const parent = parents.find(p => p.id === detailParent.id) || detailParent;
    const parentName = parent.fullName || `${parent.firstName || ''} ${parent.lastName || ''}`.trim() || 'Unknown';
    const linkedChildren = getLinkedChildren(parent);
    const isActive = parent.isActive !== false;
    return (
      <div className="admin-parents-view">
        <div className="detail-page">
          <button className="detail-back-button" onClick={() => setDetailParent(null)}>
            ◄ Back to Parents
          </button>

          <div className="detail-hero">
            <div className="detail-avatar">
              {parent.photoURL
                ? <img src={parent.photoURL} alt={parentName} />
                : (parent.firstName ? parent.firstName[0].toUpperCase() : '👨‍👩‍👧')}
            </div>
            <div className="detail-hero-info">
              <h2>{parentName}</h2>
              <p className="detail-subtitle">{parent.email || 'No email'}</p>
              <div className="detail-badges">
                <span className={`detail-badge ${isActive ? 'green' : 'red'}`}>{isActive ? 'Active' : 'Inactive'}</span>
                <span className="detail-badge gray">{linkedChildren.length} linked {linkedChildren.length === 1 ? 'child' : 'children'}</span>
              </div>
            </div>
            <div className="detail-hero-actions">
              <button className="detail-btn detail-btn-edit" onClick={() => handleEditClick(parent)}>
                Edit
              </button>
              <button
                className="detail-btn detail-btn-delete"
                onClick={async () => { if (await handleDeleteParent(parent.id)) setDetailParent(null); }}
              >
                Delete
              </button>
            </div>
          </div>

          <div className="detail-grid">
            <div className="detail-card">
              <h3>Contact &amp; Account</h3>
              <div className="detail-field">
                <span className="field-label">Name</span>
                <span className="field-value">{parentName}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Email</span>
                <span className="field-value">{parent.email || 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Phone</span>
                <span className="field-value">{parent.phoneNumber || 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Username</span>
                <span className="field-value">{parent.username || 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Status</span>
                <span className="field-value">{isActive ? 'Active' : 'Inactive'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Created</span>
                <span className="field-value">{parent.createdAt ? new Date(parent.createdAt).toLocaleDateString() : 'N/A'}</span>
              </div>
              <div className="detail-field">
                <span className="field-label">Parent ID</span>
                <span className="field-value">{parent.id}</span>
              </div>
            </div>

            <div className="detail-card">
              <h3>Linked Children ({linkedChildren.length})</h3>
              {linkedChildren.length === 0 ? (
                <p className="detail-empty">No linked children. Parents link children at signup.</p>
              ) : (
                <div className="detail-list">
                  {linkedChildren.map(child => (
                    <div key={child.id} className="detail-list-item">
                      <span className="item-name">{child.fullName || `${child.firstName || ''} ${child.lastName || ''}`.trim() || 'Unknown'}</span>
                      <span className="item-meta">{child.studentId || ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {parentEditModal}
      </div>
    );
  }

  return (
    <div className="admin-parents-view">
      <div className="view-header">
        <h2>Parents</h2>
        <button className="add-button" onClick={() => setShowAddForm(true)}>
          + Add Parent
        </button>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search parents..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      <p className="count-text">Parents: {filteredParents.length}</p>

      {showAddForm && (
        <div className="add-parent-form">
          <h3>Create New Parent</h3>
          <form onSubmit={handleAddParent}>
            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={newParent.firstName}
                  onChange={(e) => setNewParent({ ...newParent, firstName: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={newParent.lastName}
                  onChange={(e) => setNewParent({ ...newParent, lastName: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={newParent.email}
                  onChange={(e) => setNewParent({ ...newParent, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={newParent.phoneNumber}
                  onChange={(e) => setNewParent({ ...newParent, phoneNumber: e.target.value })}
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit">Create Parent</button>
            </div>
          </form>
        </div>
      )}

      {filteredParents.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👨‍👩‍👧</div>
          <h3>No Parents</h3>
          <p>No parents found</p>
        </div>
      ) : (
        <div className="parents-list">
          {filteredParents.map(parent => {
            const linkedChildren = getLinkedChildren(parent);
            return (
              <div key={parent.id} className="parent-card" onClick={() => setDetailParent(parent)}>
                <div className="parent-avatar">
                  👨‍👩‍👧
                </div>
                <div className="parent-info">
                  <h4>{parent.fullName}</h4>
                  <p>{parent.email}</p>
                  {parent.phoneNumber && <p>📞 {parent.phoneNumber}</p>}
                  <div className="linked-children">
                    <span className="children-count">{linkedChildren.length} linked children</span>
                    {linkedChildren.length > 0 && (
                      <div className="children-list">
                        {linkedChildren.map(child => (
                          <span key={child.id} className="child-tag">
                            {child.fullName}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="parent-status">
                  <span className="status-dot active"></span>
                </div>
                <button
                  className="delete-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteParent(parent.id);
                  }}
                >
                  🗑️
                </button>
              </div>
            );
          })}
        </div>
      )}

      {parentEditModal}
    </div>
  );
}

export default AdminParentsView;
